import type { PlanCode } from "./apiContracts";
import type { PrototypeApiClient } from "./apiClient";
import type { Root } from "./prototypeTypes";
import {
	completeMediaPlayerSeek,
	mountedFor,
	refreshMediaSessionPlayer,
	toggleMediaPlayer,
} from "./mediaPlayerCore";
import { formatTime, paintAudioProgressRing, playbackDurationSeconds } from "./mediaPlayerProgress";
import { getChapterCount, navigateAndPlayAudio } from "./mediaPlayerChapterPlayback";
import { playerSelectors } from "./mediaPlayerSelectors";
import { getRuntime } from './runtimeAccessor';
import { showToast } from "./toastController";
import { resolveMediaAccessDecision, presentMediaAccessDecision } from "./mediaPlayerAccessGate";
import { isKeyboardFocusedControl } from './keyboardFocus';
import {
	beginAudioHandoffCommandIntent,
	isAudioHandoffIntentCurrent,
	targetAudioHandoffIntent,
	type AudioExplicitPlayIntent,
} from './audioHandoffController';

// The player bar is global and survives article paints. Its listeners must be
// owned by the root, not by whichever article happened to paint last.
const progressWiringAbort = new WeakMap<HTMLInputElement, AbortController>();
const progressSlidersByRoot = new WeakMap<object, Set<HTMLInputElement>>();
const audioControllerAbort = new WeakMap<object, AbortController>();
const wiredAudioPlayers = new WeakMap<object, HTMLElement>();
interface AudioControllerState {
	navigationDirection: 1 | -1 | null;
	navigationGeneration: number;
	navigationIntent: AudioExplicitPlayIntent | null;
}
const controllerStates = new WeakMap<object, AudioControllerState>();

function controllerState(root: Root): AudioControllerState {
	const key = root as unknown as object;
	let state = controllerStates.get(key);
	if (!state) {
		state = { navigationDirection: null, navigationGeneration: 0, navigationIntent: null };
		controllerStates.set(key, state);
	}
	return state;
}

function progressSliders(root: Root): Set<HTMLInputElement> {
	const key = root as unknown as object;
	let sliders = progressSlidersByRoot.get(key);
	if (!sliders) {
		sliders = new Set<HTMLInputElement>();
		progressSlidersByRoot.set(key, sliders);
	}
	return sliders;
}

export function attachAudioPlayer(
  root: Root,
  api: PrototypeApiClient,
  topicId: string,
  chapterIndex: number,
  plan: PlanCode,
): void {
  const playerBar = (root as unknown as Document).querySelector?.<HTMLElement>("#webPlayer")
    ?? document.getElementById("webPlayer");
  if (!playerBar) return;
  const previous = playerBar.querySelector<HTMLButtonElement>('[data-audio-control="previous"]');
  const next = playerBar.querySelector<HTMLButtonElement>('[data-audio-control="next"]');
  wirePrev(previous ?? undefined, root, api, topicId, chapterIndex, plan);
  wireNext(next ?? undefined, root, api, topicId, chapterIndex, plan);
	wireAudioCommands(root, api, topicId, chapterIndex, plan, playerBar);
  wireProgress(playerBar, root);
	refreshMediaSessionPlayer(root, 'audio');
}

/** Removes all root-owned listeners when the application root is destroyed. */
export function detachAudioPlayer(root: Root): void {
	const key = root as unknown as object;
	audioControllerAbort.get(key)?.abort();
	audioControllerAbort.delete(key);
	for (const slider of progressSlidersByRoot.get(key) ?? []) {
		progressWiringAbort.get(slider)?.abort();
		progressWiringAbort.delete(slider);
		slider.oninput = null;
		slider.onchange = null;
	}
	progressSlidersByRoot.delete(key);
	const player = wiredAudioPlayers.get(key);
	const previous = player?.querySelector<HTMLButtonElement>('[data-audio-control="previous"]');
	const next = player?.querySelector<HTMLButtonElement>('[data-audio-control="next"]');
	if (previous) previous.onclick = null;
	if (next) next.onclick = null;
	wiredAudioPlayers.delete(key);
	controllerStates.delete(key);
}

function wireAudioCommands(root: Root, api: PrototypeApiClient, topicId: string, chapterIndex: number, plan: PlanCode, playerBar: HTMLElement): void {
	const key = root as unknown as object;
	// Article paints can call this repeatedly. The root command bus is stable;
	// only rebind it if a genuine global player element was replaced.
	if (wiredAudioPlayers.get(key) === playerBar) return;
	audioControllerAbort.get(key)?.abort();
	const abort = typeof AbortController === 'undefined' ? null : new AbortController();
	if (abort) audioControllerAbort.set(key, abort);
	wiredAudioPlayers.set(key, playerBar);
	const options = abort ? ({ signal: abort.signal } as AddEventListenerOptions) : undefined;
	const target = root as Document | HTMLElement;
	target.addEventListener('deepmox:audio-prev', () => moveAudioChapter(root, api, topicId, chapterIndex, currentPlan(root, plan), -1), options);
	target.addEventListener('deepmox:audio-next', () => moveAudioChapter(root, api, topicId, chapterIndex, currentPlan(root, plan), 1), options);
	// Absolute navigation is anchored to the mounted audio, never the article
	// currently being browsed. That distinction prevents a late Topic A token
	// or a Topic B page paint from stealing the active player.
	target.addEventListener('deepmox:audio-goto', (event) => {
		const commandIntent = beginAudioHandoffCommandIntent(root);
		const index = (event as CustomEvent<{ index?: number }>).detail?.index;
		if (typeof index !== 'number' || !Number.isInteger(index) || index < 1) return;
		const runtime = getRuntime(root);
		const mounted = mountedFor(root, 'audio');
		const activeTopicId = mounted?.topicId || runtime?.activeCourseId || topicId;
		const activeChapterIndex = mounted?.chapterIndex ?? runtime?.activeChapterIndex ?? chapterIndex;
		if (activeChapterIndex === index) return;
		// Defense-in-depth L1: playlist rows already gate locked chapters and
		// never dispatch this event, but resume / deep-link / any future caller
		// could. Mirror the locked-row behavior — present the paywall, leave the
		// runtime + URL + audio untouched.
		const decision = resolveMediaAccessDecision(root, 'audio', activeTopicId, index);
		if (decision.kind !== 'allow') {
			presentMediaAccessDecision(root, decision, 'audio-goto');
			return;
		}
		const navigationIntent = targetAudioHandoffIntent(root, commandIntent, { topicId: activeTopicId, chapterIndex: index });
		if (navigationIntent) navigateAndPlayAudio(root, activeTopicId, index, currentPlan(root, plan), api, navigationIntent);
	}, options);
	// Space toggles audio play/pause only while an audio bar is mounted.
	// Every guard below falls through WITHOUT preventDefault so the key keeps
	// native browser behavior (page scroll) when no audio bar exists.
	const keyboardDoc = root instanceof Document ? root : root.ownerDocument ?? document;
	keyboardDoc.addEventListener('keydown', (event: KeyboardEvent) => {
		if (event.key !== ' ' && event.key !== 'Spacebar') return;
		// The fullscreen video surface owns Space through its own capture handler.
		if (keyboardDoc.getElementById(playerSelectors.video.container)?.classList.contains('active')) return;
		if (!mountedFor(root, 'audio')) return;
		const focused = event.target as HTMLElement | null;
		// Editable fields always own Space: a pointer click into a search box
		// starts an active editing session, not pointer residue, so the
		// modality gate below must never hand those keystrokes to the player.
		if (focused?.closest('input, textarea, [contenteditable]:not([contenteditable="false"])')) return;
		// Interactive controls keep native Space activation under keyboard
		// focus only. A pointer click leaves a control focused without a focus
		// ring; Space would silently re-activate it on keyup (nav tab
		// re-navigates, list reloads), so the player takes the key instead.
		if (focused?.closest('select, button, a[href], summary, [role="button"], [role="link"], [role="tab"], [role="switch"], [role="option"], [data-keyboard-action="true"]') && isKeyboardFocusedControl(focused)) return;
		event.preventDefault();
		if (event.repeat) return;
		toggleMediaPlayer(root, 'audio');
	}, options);
}

function wirePrev(
  button: HTMLButtonElement | undefined,
  root: Root,
  api: PrototypeApiClient,
  topicId: string,
  chapterIndex: number,
  plan: PlanCode,
) {
  if (!button) return;
  button.onclick = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
		moveAudioChapter(root, api, topicId, chapterIndex, currentPlan(root, plan), -1);
  };
  button.style.cursor = "pointer";
}

function wireNext(
  button: HTMLButtonElement | undefined,
  root: Root,
  api: PrototypeApiClient,
  topicId: string,
  chapterIndex: number,
  plan: PlanCode,
) {
  if (!button) return;
  button.onclick = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
		moveAudioChapter(root, api, topicId, chapterIndex, currentPlan(root, plan), 1);
  };
  button.style.cursor = "pointer";
}

function currentPlan(root: Root, fallback: PlanCode): PlanCode {
	return getRuntime(root)?.currentPlan ?? fallback;
}

function moveAudioChapter(
	root: Root,
	api: PrototypeApiClient,
	fallbackTopicId: string,
	fallbackChapterIndex: number,
	plan: PlanCode,
	direction: 1 | -1,
): void {
	const state = controllerState(root);
	if (direction !== state.navigationDirection || !state.navigationIntent || !isAudioHandoffIntentCurrent(root, state.navigationIntent)) {
		state.navigationDirection = direction;
		state.navigationGeneration += 1;
		state.navigationIntent = beginAudioHandoffCommandIntent(root);
	}
	const intent = state.navigationIntent ?? beginAudioHandoffCommandIntent(root);
	state.navigationIntent = intent;
	const commandGeneration = state.navigationGeneration;
	const current = () => {
		const runtime = getRuntime(root);
		const mounted = mountedFor(root, 'audio');
		const runtimeChapterIndex = mounted?.chapterIndex ?? runtime?.activeChapterIndex;
		return {
			topicId: mounted?.topicId || runtime?.activeCourseId || fallbackTopicId,
			chapterIndex: typeof runtimeChapterIndex === 'number' && Number.isFinite(runtimeChapterIndex) && runtimeChapterIndex > 0
				? runtimeChapterIndex
				: fallbackChapterIndex,
		};
	};

	if (direction < 0) {
		const target = current();
		const previousIndex = Math.max(1, target.chapterIndex - 1);
		if (previousIndex === target.chapterIndex) {
			showToast({ type: 'info', message: "You're already on the first chapter.", durationMs: 2200 });
			return;
		}
		// L1 paywall gate: mirror the locked-playlist-row behaviour — present
		// the paywall, leave runtime + URL + audio untouched.
		const decision = resolveMediaAccessDecision(root, 'audio', target.topicId, previousIndex);
		if (decision.kind !== 'allow') {
			presentMediaAccessDecision(root, decision, 'audio-prev');
			return;
		}
		const navigationIntent = targetAudioHandoffIntent(root, intent, { topicId: target.topicId, chapterIndex: previousIndex });
		if (navigationIntent) navigateAndPlayAudio(root, target.topicId, previousIndex, plan, api, navigationIntent);
		return;
	}

	const requestedTopicId = current().topicId;
	void getChapterCount(api, requestedTopicId).then((total) => {
		if (commandGeneration !== state.navigationGeneration || state.navigationDirection !== direction || !isAudioHandoffIntentCurrent(root, intent)) return;
		const target = current();
		if (target.topicId !== requestedTopicId) return;
		const max = total > 0 ? total : target.chapterIndex + 1;
		const nextIndex = Math.min(max, target.chapterIndex + 1);
		if (nextIndex === target.chapterIndex) {
			showToast({ type: 'info', message: "You're already on the last chapter.", durationMs: 2200 });
			return;
		}
		// L1 paywall gate: mirror the locked-playlist-row behaviour — present
		// the paywall, leave runtime + URL + audio untouched.
		const decision = resolveMediaAccessDecision(root, 'audio', target.topicId, nextIndex);
		if (decision.kind !== 'allow') {
			presentMediaAccessDecision(root, decision, 'audio-next');
			return;
		}
		const navigationIntent = targetAudioHandoffIntent(root, intent, { topicId: target.topicId, chapterIndex: nextIndex });
		if (navigationIntent) navigateAndPlayAudio(root, target.topicId, nextIndex, plan, api, navigationIntent);
	});
}

function wireProgress(playerBar: HTMLElement, root: Root) {
  const slider = playerBar.querySelector<HTMLInputElement>("#audioProgressSlider");
  if (!slider) return;
  progressWiringAbort.get(slider)?.abort();
  const abort = new AbortController();
  progressWiringAbort.set(slider, abort);
	progressSliders(root).add(slider);
  const listenerOptions = { capture: true, signal: abort.signal } as AddEventListenerOptions;
  const progress = playerBar.querySelector<HTMLElement>("#playerProgress");
  const time = playerBar.querySelector<HTMLElement>("#audioTime");
	const ring = playerBar.querySelector<HTMLElement>('#playerThumbProgress');
  const fraction = () => Number(slider.value) / 100;
  // A range drag is a local UI interaction until its *final* seek settles.
  // In particular, do not assign audio.currentTime from `input`: Safari can
  // report the old currentTime between those assignments and feed it back into
  // the range. The marker is intentionally on this concrete slider instead
  // of a module-wide flag, so duplicate/reparented players cannot block one
  // another's media UI.
  type ScrubState = 'idle' | 'dragging' | 'awaiting-seek';
  let scrubState: ScrubState = 'idle';
  let activePointerId: number | null = null;
  let committedValue: string | null = null;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  const media = () => mountedFor(root, 'audio');
  const progressShell = slider.closest<HTMLElement>('.wp-progress');
  const ownsUi = () => scrubState !== 'idle';
  const setUiOwnership = (state: ScrubState) => {
    scrubState = state;
    if (state === 'idle') delete slider.dataset.audioScrubOwner;
    else slider.dataset.audioScrubOwner = state;
    progressShell?.classList.toggle('is-scrubbing', state === 'dragging');
		playerBar.classList.toggle('is-audio-scrubbing', state === 'dragging');
    if (progress) progress.style.transition = state === 'dragging' ? 'none' : '';
  };
  const clearPendingSettle = () => {
    if (settleTimer !== undefined) clearTimeout(settleTimer);
    settleTimer = undefined;
  };
  const releaseUiOwnership = () => {
    clearPendingSettle();
    setUiOwnership('idle');
  };
  abort.signal.addEventListener('abort', releaseUiOwnership, { once: true });
  // Live preview writes only the *mounted* player's DOM. It never seeks the
  // media element, so fill, thumb and time remain one frame under the finger.
  const paintPreview = () => {
    const mounted = media();
    const duration = mounted ? playbackDurationSeconds(mounted) : 0;
    const target = duration * fraction();
    const pct = duration > 0 ? Math.min(100, Math.max(0, (target / duration) * 100)) : 0;
    if (progress) progress.style.width = `${pct}%`;
		paintAudioProgressRing(ring, pct, duration);
    slider.setAttribute("aria-valuetext", `${formatTime(target)} of ${formatTime(duration)}`);
    if (time) time.textContent = `${formatTime(target)} / ${formatTime(duration)}`;
  };
  // Keep ownership through the final seek. A stale timeupdate is harmless
  // while `awaiting-seek`; seeked, or a bounded fallback, hands it back.
  const commitDrag = () => {
    if (scrubState !== 'dragging') return;
    activePointerId = null;
    committedValue = slider.value;
    setUiOwnership('awaiting-seek');
    const mounted = media();
    if (!mounted) {
      releaseUiOwnership();
      return;
    }
    const settle = () => releaseUiOwnership();
    mounted.element.addEventListener('seeked', settle, { once: true, signal: abort.signal });
    settleTimer = setTimeout(settle, 1_500);
    completeMediaPlayerSeek(root, 'audio', fraction());
  };
  slider.oninput = () => {
    // iOS native ranges can deliver input before pointerdown. touchstart and
    // :active cover that path without treating keyboard changes as a drag.
    if (!ownsUi() && slider.matches(':active')) setUiOwnership('dragging');
    paintPreview();
  };
  slider.onchange = () => {
    if (scrubState === 'dragging') {
      commitDrag();
      return;
    }
    // Native range controls dispatch change after pointer/touch end. Its
    // accompanying release is authoritative; do not duplicate the seek.
    if (committedValue === slider.value) {
      committedValue = null;
      return;
    }
    completeMediaPlayerSeek(root, "audio", fraction());
  };
  const beginPointerScrub = (event: PointerEvent) => {
    if (scrubState === 'awaiting-seek') releaseUiOwnership();
    if (scrubState === 'dragging') return;
    activePointerId = event.pointerId;
    committedValue = null;
    setUiOwnership('dragging');
    try {
      slider.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is unavailable for a few synthetic/browser edge
      // cases; the document-level end listeners below remain the fallback.
    }
  };
  const endPointerScrub = (event: PointerEvent) => {
    if (scrubState !== 'dragging' || (activePointerId !== null && event.pointerId !== activePointerId)) return;
    try {
      if (slider.hasPointerCapture(event.pointerId)) slider.releasePointerCapture(event.pointerId);
    } catch {
      // Releasing after a native cancellation is intentionally best-effort.
    }
    commitDrag();
  };
  const beginTouchScrub = () => {
    if (scrubState === 'idle') setUiOwnership('dragging');
  };
  const endTouchScrub = () => commitDrag();
  const endLostPointerCapture = () => commitDrag();
  // Capture makes release reliable outside the narrow track. Touch listeners
  // cover iOS range controls that never surface pointerdown; every end route
  // converges on the state-gated once-only commit.
  slider.addEventListener("pointerdown", beginPointerScrub, listenerOptions);
  slider.addEventListener("pointerup", endPointerScrub, listenerOptions);
  slider.addEventListener("pointercancel", endPointerScrub, listenerOptions);
  slider.addEventListener("lostpointercapture", endLostPointerCapture, listenerOptions);
  slider.addEventListener('touchstart', beginTouchScrub, listenerOptions);
  slider.addEventListener('touchend', endTouchScrub, listenerOptions);
  slider.addEventListener('touchcancel', endTouchScrub, listenerOptions);
  playerBar.ownerDocument?.addEventListener("pointerup", endPointerScrub, listenerOptions);
  playerBar.ownerDocument?.addEventListener("pointercancel", endPointerScrub, listenerOptions);
  playerBar.ownerDocument?.addEventListener('touchend', endTouchScrub, listenerOptions);
  playerBar.ownerDocument?.addEventListener('touchcancel', endTouchScrub, listenerOptions);
}
